// In-browser reminder runner.
// Checks every 30 seconds for any active habit whose `reminder_time` (HH:MM) matches
// the current local time and fires a Notification. Tracks "already shown today"
// in localStorage so each reminder fires at most once per day.
//
// NOTE: localStorage holds only non-sensitive scheduling state (habit_id : ISO
// date pairs). No tokens or PII.

import { supabase } from './supabase';
import { isScheduledOn, todayISO } from './dates';

const SHOWN_KEY = 'mfh:reminders-shown';

const readShown = () => {
  try { return JSON.parse(localStorage.getItem(SHOWN_KEY) || '{}'); }
  catch { return {}; }
};
const writeShown = (obj) => localStorage.setItem(SHOWN_KEY, JSON.stringify(obj));

const purgeOld = () => {
  const today = todayISO();
  const shown = readShown();
  const pruned = {};
  for (const [k, v] of Object.entries(shown)) {
    if (v === today) pruned[k] = v;
  }
  writeShown(pruned);
};

const hhmmNow = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return await Notification.requestPermission();
};

export const isNotificationsEnabled = () =>
  typeof Notification !== 'undefined' && Notification.permission === 'granted';

const fire = (habit) => {
  if (!isNotificationsEnabled()) return;
  const body =
    habit.type === 'binary'
      ? 'Time for your habit.'
      : habit.type === 'time'
      ? `Time to focus for ${habit.target_value} min.`
      : `Aim for ${habit.target_value}${habit.unit ? ' ' + habit.unit : ''} today.`;
  try {
    // Use ServiceWorker registration where available so notifications survive
    // app focus changes; fall back to direct Notification.
    if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(`${habit.name} · My Flow Habit`, {
          body,
          tag: `mfh-reminder-${habit.id}`,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          data: { habitId: habit.id, url: '/today' },
        });
      }).catch(() => {
        new Notification(`${habit.name} · My Flow Habit`, { body });
      });
    } else {
      new Notification(`${habit.name} · My Flow Habit`, { body });
    }
  } catch (err) {
    // Notification API can throw on some browsers / permissions states.
    console.warn('[mfh] reminder notification failed:', err);
  }
};

let intervalId = null;

export const startReminderRunner = (userId) => {
  stopReminderRunner();
  purgeOld();

  const tick = async () => {
    if (!isNotificationsEnabled() || !userId) return;
    const now = hhmmNow();
    const today = todayISO();
    const shown = readShown();
    const { data: habits } = await supabase
      .from('habits')
      .select('id,name,type,target_value,unit,schedule_type,schedule_days,time_of_day,reminder_time,color,icon')
      .is('archived_at', null)
      .not('reminder_time', 'is', null);
    if (!habits) return;
    for (const h of habits) {
      if (h.reminder_time !== now) continue;
      if (!isScheduledOn(h, new Date())) continue;
      const key = `${h.id}:${today}`;
      if (shown[key]) continue;
      fire(h);
      shown[key] = today;
    }
    writeShown(shown);
  };
  // Run once immediately, then every 30s
  tick();
  intervalId = setInterval(tick, 30 * 1000);
};

export const stopReminderRunner = () => {
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
};
